import { Heart, Star, Calendar, MessageCircle, Sparkles, Zap } from "lucide-react";

const FEATURES = [
  {
    icon: Heart,
    title: "Warm thank-yous, auto-sent",
    body: "Every visit ends with a personal note that lands in minutes — signed by the person your customer just saw, not a bot.",
    span: "lg:col-span-6",
    variant: "sage",
  },
  {
    icon: Star,
    title: "5-star review requests",
    body: "Ask at exactly the right moment. NextVisit picks the moment your customer feels best about the visit.",
    span: "lg:col-span-6",
    variant: "ivory",
  },
  {
    icon: Calendar,
    title: "Rebooking reminders that book",
    body: "Smart nudges timed to each customer's rhythm — every 4 weeks for a color, every 6 for a cleaning. They rebook. You don't have to ask.",
    span: "lg:col-span-4",
    variant: "ivory",
  },
  {
    icon: MessageCircle,
    title: "Win-back campaigns",
    body: "Haven't seen someone in a while? A friendly offer, sent with heart, turns lapsed guests into loyal ones.",
    span: "lg:col-span-4",
    variant: "sage",
  },
  {
    icon: Sparkles,
    title: "Messaging flows without code",
    body: "Drag steps into a flow. Approve every send. Or trust auto-mode when you're busy behind the chair.",
    span: "lg:col-span-4",
    variant: "ivory",
  },
  {
    icon: Zap,
    title: "Live Square sync, more coming",
    body: "Square Appointments syncs automatically today — customers and bookings flow in via API + webhooks. Vagaro, Fresha, Mindbody, Acuity, Calendly, and Google Calendar are on the roadmap. Not on Square? Add customers by hand or paste them from CSV.",
    span: "lg:col-span-12",
    variant: "apricot",
  },
];

function Card({ feature }) {
  const Icon = feature.icon;
  const styles =
    feature.variant === "sage"
      ? "bg-[#A8BFA3] text-[#183D36]"
      : feature.variant === "apricot"
      ? "bg-[#F4B183] text-[#252525]"
      : "bg-white border border-[#E5E0D8] text-[#252525]";

  return (
    <div className={`rounded-3xl p-8 sm:p-10 transition-all hover:-translate-y-1 hover:shadow-lg ${styles} ${feature.span}`}
      data-testid={`feature-card-${feature.title.split(' ')[0].toLowerCase()}`}>
      <div className="w-11 h-11 rounded-2xl bg-white/40 backdrop-blur flex items-center justify-center mb-6">
        <Icon size={20} strokeWidth={1.75} className="text-[#183D36]" />
      </div>
      <h3 className="font-heading text-2xl mb-3 leading-tight">{feature.title}</h3>
      <p className="text-base leading-relaxed opacity-90 max-w-md">{feature.body}</p>
    </div>
  );
}

export default function Features() {
  return (
    <section id="features" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-4">Everything you need, nothing you don't</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#183D36]">
            One quiet workflow that keeps your calendar full.
          </h2>
          <p className="mt-5 text-lg text-[#4A4A4A] leading-relaxed">
            Follow-ups aren't the fun part of the work. So we made them run themselves — warmly, in your voice, at the right time.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {FEATURES.map((f) => <Card key={f.title} feature={f} />)}
        </div>
      </div>
    </section>
  );
}
